import type { ZodTypeAny } from "zod";

import {
  ECompanyMemberRoleStatus,
  ECompanyRoleCode,
  schemes,
  zSchema,
} from "@zeal/common";

const patchMemberLifecycleStatusSchema = zSchema.enum([
  ECompanyMemberRoleStatus.Active,
  ECompanyMemberRoleStatus.Deactivated,
]);

const rolesArraySchema = zSchema
  .array(zSchema.nativeEnum(ECompanyRoleCode))
  .min(1);

const companyMemberPathFields = {
  companyID: schemes.companyID(),
  memberID: zSchema.string().min(1),
};

export const companyMemberPathParamsSchema = zSchema.object(
  companyMemberPathFields
);

/** DELETE /api/companies/:companyId/members/:memberId — same path fields as patch. */
export const deleteCompanyMemberRequestSchema = companyMemberPathParamsSchema;

const absentPatchBodyFieldSchema = zSchema
  .union([zSchema.undefined(), zSchema.null()])
  .optional();

const patchCompanyMemberBranchSchema = (body: {
  status?: ZodTypeAny;
  roles?: ZodTypeAny;
  first_name?: ZodTypeAny;
  last_name?: ZodTypeAny;
}) =>
  zSchema
    .object({
      ...companyMemberPathFields,
      status: body.status ?? absentPatchBodyFieldSchema,
      roles: body.roles ?? absentPatchBodyFieldSchema,
      first_name: body.first_name ?? absentPatchBodyFieldSchema,
      last_name: body.last_name ?? absentPatchBodyFieldSchema,
    })
    .strict();

const patchCompanyMemberStatusSchema = patchCompanyMemberBranchSchema({
  status: patchMemberLifecycleStatusSchema,
});

const patchCompanyMemberRolesSchema = patchCompanyMemberBranchSchema({
  roles: rolesArraySchema,
});

const patchCompanyMemberFirstNameSchema = patchCompanyMemberBranchSchema({
  first_name: zSchema.string().trim(),
});

const patchCompanyMemberLastNameSchema = patchCompanyMemberBranchSchema({
  last_name: zSchema.string().trim(),
});

const patchCompanyMemberFullNameSchema = patchCompanyMemberBranchSchema({
  first_name: zSchema.string().trim(),
  last_name: zSchema.string().trim(),
});

const patchCompanyMemberRolesFirstNameSchema = patchCompanyMemberBranchSchema({
  roles: rolesArraySchema,
  first_name: zSchema.string().trim(),
});

const patchCompanyMemberRolesLastNameSchema = patchCompanyMemberBranchSchema({
  roles: rolesArraySchema,
  last_name: zSchema.string().trim(),
});

const patchCompanyMemberRolesFullNameSchema = patchCompanyMemberBranchSchema({
  roles: rolesArraySchema,
  first_name: zSchema.string().trim(),
  last_name: zSchema.string().trim(),
});

export const patchCompanyMemberRequestSchema = zSchema.union([
  patchCompanyMemberStatusSchema,
  patchCompanyMemberRolesSchema,
  patchCompanyMemberFirstNameSchema,
  patchCompanyMemberLastNameSchema,
  patchCompanyMemberFullNameSchema,
  patchCompanyMemberRolesFirstNameSchema,
  patchCompanyMemberRolesLastNameSchema,
  patchCompanyMemberRolesFullNameSchema,
]);

export type TPatchCompanyMemberRequest = zSchema.infer<
  typeof patchCompanyMemberRequestSchema
>;

export type TPatchCompanyMemberBody = Pick<
  TPatchCompanyMemberRequest,
  "roles" | "first_name" | "last_name" | "status"
>;
